/**
 * PRODUCT EVENT CATALOG — typed names + payload shapes for the 7 Phase-1 events
 *
 * The runbook (§7 step 49) calls for exactly seven events as the v1 baseline.
 * Adding an 8th? Treat it as a product-foundation change: append to the union,
 * write a test, document it in `docs/OBSERVABILITY.md`. Removing or renaming?
 * That's a breaking change for the analytics warehouse — coordinate with the
 * data team before merging.
 *
 * Why centralized:
 *   - One import site means one place to grep for "what events ship today".
 *   - Compile-time safety: a typo in an event name fails `tsc`, not silently
 *     drops a row in PostHog.
 *   - The PII allowlist below feeds straight into `lib/log/sanitize.ts` so the
 *     event payload contract IS the privacy contract.
 */

export const EVENT_NAMES = [
    "user_signed_up",
    "user_signed_in",
    "invite_sent",
    "invite_redeemed",
    "company_added",
    "strategy_soap_generated",
    "monitoring_snapshot_uploaded",
] as const;

export type EventName = (typeof EVENT_NAMES)[number];

export interface UserSignedUpProps {
    user_id_hash: string;
    tenant_id: string;
    auth_provider: "entra" | "supabase" | "mock";
    bootstrapped?: boolean;
}

export interface UserSignedInProps {
    user_id_hash: string;
    tenant_id: string;
    auth_provider: "entra" | "supabase" | "mock";
}

export interface InviteSentProps {
    tenant_id: string;
    invited_user_id: string;
    invited_role: "admin" | "editor" | "viewer";
    actor_user_id_hash: string;
}

export interface InviteRedeemedProps {
    user_id_hash: string;
    tenant_id: string;
    invited_role: "admin" | "editor" | "viewer";
}

export interface CompanyAddedProps {
    tenant_id: string;
    user_id_hash: string;
    company_id: string;
    tracking_mode?: "full_workspace" | "monitoring_only";
}

export interface StrategySoapGeneratedProps {
    tenant_id: string;
    user_id_hash: string;
    company_id: string;
    duration_ms: number;
    skip_validation: boolean;
}

export interface MonitoringSnapshotUploadedProps {
    tenant_id: string;
    user_id_hash: string;
    snapshot_id: string;
    company_id: string;
    metric_count: number;
}

export interface EventPayloadMap {
    user_signed_up: UserSignedUpProps;
    user_signed_in: UserSignedInProps;
    invite_sent: InviteSentProps;
    invite_redeemed: InviteRedeemedProps;
    company_added: CompanyAddedProps;
    strategy_soap_generated: StrategySoapGeneratedProps;
    monitoring_snapshot_uploaded: MonitoringSnapshotUploadedProps;
}

export type AnyEvent = {
    [K in EventName]: { name: K; props: EventPayloadMap[K] };
}[EventName];

export function buildEvent<K extends EventName>(
    name: K,
    props: EventPayloadMap[K],
): AnyEvent {
    return { name, props } as AnyEvent;
}

export const EVENT_KEEP_KEYS: readonly string[] = [
    "auth_provider",
    "bootstrapped",
    "invited_user_id",
    "invited_role",
    "actor_user_id_hash",
    "tracking_mode",
    "skip_validation",
    "metric_count",
];
